import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@components/toast/Toast";
import { useCustomerDetails, useSaveSmartNote } from "@api/customersController";
import {
  fetchConversationTurns,
  useAcceptTask,
  useCancelAction,
  useDecideAction,
  warmContext,
} from "@api/intelligenceController";
import useSignalChat from "./useSignalChat";
import { getSuggestionComponent } from "./suggestionComponents";
import { cancelledActivityCopy } from "./cancellationCopy";
import { isActionBacked, isSendEmailType } from "./DraftSuggestionCard";
import {
  buildAmendments,
  buildEmailAmendments,
  editablePaymentStep,
  isStatementStep,
} from "./actionAmendments";

const EMPTY_DRAFT = {
  text: "",
  mentionedCustomerIds: [],
  mentionedUserIds: [],
  mentions: [],
  llmText: "",
};

// The requested amount off an action-backed payment request's composed payment.request.send
// step — best-effort, for the optimistic activity-feed row only (the canonical row reconciles
// from the realtime ActivityCreated event regardless).
function paymentRequestAmount(steps) {
  return (
    steps?.find((s) => s.stepType === "payment.request.send")?.payload
      ?.amount_requested ?? null
  );
}

// The conversation + suggestion brain shared by both Smart Note layouts (V1 floating cards, V2
// unified account view). Owns the running thread, the selected/analysed customer, every
// accept/reject/edit flow, and the optimistic activity-feed writes — so a layout only has to
// render state and wire callbacks. Keeping one controller means a fix lands in both views at once.
export default function useSmartNoteController({ isOpen, onClose, initialCustomer = null }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // The customer the running thread belongs to (snapshotted on submit) — context for
  // accepting/rejecting a suggestion (customerId, note-card attribution).
  const [analysedCustomer, setAnalysedCustomer] = useState(null);

  // Entry points other than the @-search open Smart Note with only { id, name, accountCode }, but the
  // request modals derive bucket amounts and contacts from the full record — without it they show £0.
  const requestCustomerBase = analysedCustomer ?? selectedCustomer;
  const { data: requestCustomerDetail } = useCustomerDetails(requestCustomerBase?.id);
  const requestCustomer =
    requestCustomerBase && requestCustomerDetail
      ? { ...requestCustomerBase, ...requestCustomerDetail }
      : requestCustomerBase;

  // Pre-select a customer when the flow is opened for one (e.g. a task's "view context", or the
  // spacebar landing on a highlighted account); the free spacebar path passes none.
  useEffect(() => {
    if (isOpen && initialCustomer) setSelectedCustomer(initialCustomer);
  }, [isOpen, initialCustomer]);

  // Remount the editor to clear it after each submitted turn.
  const [editorKey, setEditorKey] = useState(0);
  // Text to seed the (remounted) composer with — set when an autocomplete suggestion is accepted, so the
  // chosen prompt drops in ready to edit or send. Cleared on submit/close so a later remount starts empty.
  const [composerSeed, setComposerSeed] = useState(null);

  // Per-suggestion state, keyed `${messageId}:${signalIndex}`. Accepted suggestions stay on
  // screen and flip to a tick; rejected ones flip to a greyed-out cross.
  const [done, setDone] = useState(new Set());
  const [rejected, setRejected] = useState(new Set());
  const [pending, setPending] = useState(new Set());

  const [editTarget, setEditTarget] = useState(null);
  const [editDocsTarget, setEditDocsTarget] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const [paymentRequestId, setPaymentRequestId] = useState(null);
  const [planActionId, setPlanActionId] = useState(null);

  // When an accept writes an optimistic activity row, this names that row and bumps a counter.
  // The V2 layout watches the counter to slide its detail panel to the Activity tab, and hands the
  // id to the feed so only that row plays the "squeeze in" entrance. (V1 ignores both.)
  const [enterActivityId, setEnterActivityId] = useState(null);
  const [activityPulse, setActivityPulse] = useState(0);

  const queryClient = useQueryClient();
  // Imperative handle on the context panel / activity feed — the task accept asks it to reconcile
  // the feed and Ontik read if the realtime hint is missed.
  const contextPanelRef = useRef(null);
  const saveMutation = useSaveSmartNote();
  const decideAction = useDecideAction();
  const acceptTask = useAcceptTask();
  const cancelAction = useCancelAction();
  const {
    messages,
    isThinking,
    thinkingLabel,
    activeConversationId,
    send,
    reset,
    loadThread,
    appendSuggestion,
    lastCardMs,
  } = useSignalChat();

  // Which past thread is currently being fetched (so the rail can show a spinner on that row).
  const [loadingConversationId, setLoadingConversationId] = useState(null);

  // Everything scoped to the customer currently being analysed: the thread, its
  // suggestions, and any in-flight edit. Cleared on customer switch and on close.
  const clearConversation = useCallback(() => {
    reset();
    setAnalysedCustomer(null);
    setDone(new Set());
    setRejected(new Set());
    setPending(new Set());
    setEditTarget(null);
    setEditDocsTarget(null);
    setComposerSeed(null);
  }, [reset]);

  // Reopen a past thread from the history rail: fetch its transcript and paint it read-only, adopting its
  // id so the next turn continues it. Scoped to the selected customer — the server rejects a thread that
  // isn't this user's. Guarded against races: only the latest click's result is applied.
  const loadRequestRef = useRef(0);
  const loadConversation = useCallback(
    async (conversationId) => {
      const customer = selectedCustomer;
      if (!customer?.id || !conversationId) return;
      const token = ++loadRequestRef.current;
      setLoadingConversationId(conversationId);
      try {
        const turns = await fetchConversationTurns(customer.id, conversationId);
        if (loadRequestRef.current !== token) return;
        loadThread(customer.id, conversationId, turns);
        setAnalysedCustomer(customer);
        setDone(new Set());
        setRejected(new Set());
        setPending(new Set());
        setEditTarget(null);
        setEditDocsTarget(null);
      } catch {
        if (loadRequestRef.current === token)
          toast({ description: "That conversation couldn't be opened — please try again." });
      } finally {
        if (loadRequestRef.current === token) setLoadingConversationId(null);
      }
    },
    [selectedCustomer, loadThread],
  );

  // Switching accounts abandons the previous customer's thread.
  useEffect(() => {
    clearConversation();
  }, [selectedCustomer?.id, clearConversation]);

  // Optimistically lift a conversation to the top of the history list the instant a turn starts, so a
  // continued thread jumps up immediately rather than waiting the whole turn for the post-completion
  // refetch. No-op for a thread not yet in the list (a brand-new chat — already the pinned Current entry);
  // the refetch on completion reconciles with the server's real timestamp.
  const bumpConversationToTop = useCallback(
    (conversationId) => {
      const customerId = selectedCustomer?.id;
      if (!customerId || !conversationId) return;
      queryClient.setQueryData(["customerConversations", customerId], (old) => {
        if (!Array.isArray(old)) return old;
        const existing = old.find((conversation) => conversation.conversationId === conversationId);
        if (!existing) return old;
        return [
          { ...existing, updatedAt: new Date().toISOString() },
          ...old.filter((conversation) => conversation.conversationId !== conversationId),
        ];
      });
    },
    [selectedCustomer?.id, queryClient],
  );

  // A turn starting lifts its thread to the top of the rail right away; a turn finishing refetches so a
  // brand-new conversation appears and the server's real ordering/timestamp reconciles the optimistic bump.
  const wasThinkingRef = useRef(false);
  useEffect(() => {
    if (isThinking && !wasThinkingRef.current) {
      bumpConversationToTop(activeConversationId);
    }
    if (wasThinkingRef.current && !isThinking && selectedCustomer?.id) {
      queryClient.invalidateQueries({ queryKey: ["customerConversations", selectedCustomer.id] });
      queryClient.invalidateQueries({ queryKey: ["suggestionSlots", selectedCustomer.id] });
    }
    wasThinkingRef.current = isThinking;
  }, [isThinking, activeConversationId, selectedCustomer?.id, bumpConversationToTop, queryClient]);

  // The "Ontik read" holds the composer's cycling example placeholder back until it has finished
  // typing out — so only one typewriter runs at a time. Keyed by customer rather than reset in an effect:
  // a cached read reports ready from the child's effect, which runs before (and was wiped by) our reset.
  const [readReadyFor, setReadReadyFor] = useState(null);
  const readReady = !!selectedCustomer && readReadyFor === selectedCustomer.id;
  const setReadReady = useCallback(
    (ready, customerId) => setReadReadyFor(ready ? customerId : null),
    [],
  );

  const canSubmit = !!selectedCustomer && draft.text.trim().length > 0;

  // Preload the customer's context on the FIRST keystroke of a message, so the gather runs while
  // the user finishes composing and is cached by send-time (~1.4s off the turn's Context step).
  const handleDraftChange = (next) => {
    if (selectedCustomer?.id && !draft.text && next.text) warmContext(selectedCustomer.id);
    setDraft(next);
  };

  const remountEditor = () => setEditorKey((k) => k + 1);

  // Accept an autocomplete suggestion: drop its full text into the composer (via a fresh editor mount) so
  // the user can tweak it or send it straight away. Used by Tab.
  const acceptAutocompleteSuggestion = useCallback((text) => {
    if (!text) return;
    setComposerSeed({ text });
    setDraft({ ...EMPTY_DRAFT, text });
    remountEditor();
  }, []);

  // Send an autocomplete suggestion straight away — clicking a suggestion commits it like pressing Enter.
  // Goes directly to the thread (no composer round-trip); suggestions are plain text with no mentions.
  const sendAutocompleteSuggestion = (text) => {
    const trimmed = (text ?? "").trim();
    if (!selectedCustomer || !trimmed) return;
    send({ customerId: selectedCustomer.id, text: trimmed, displayText: trimmed, mentions: [] });
    setAnalysedCustomer(selectedCustomer);
    setDraft(EMPTY_DRAFT);
    setComposerSeed(null);
    remountEditor();
  };

  const handleSubmit = () => {
    const text = draft.text.trim();
    if (!selectedCustomer || !text) return;

    // Send the <colleague>-tagged form to the LLM, but show the raw "@name" text.
    const llmText = (draft.llmText || draft.text).trim();
    const mentions = draft.mentions;

    send({
      customerId: selectedCustomer.id,
      text: llmText,
      displayText: draft.text,
      mentions,
    });
    setAnalysedCustomer(selectedCustomer);
    setDraft(EMPTY_DRAFT);
    setComposerSeed(null);
    remountEditor();
  };

  const handleSave = (content, mentions, conversationId, onSuccess, onSettled) => {
    const customerId = selectedCustomer?.id ?? null;
    saveMutation.mutate(
      {
        customerId,
        content,
        mentionedUserIds: mentions.map((m) => m.id),
        conversationId,
        taggedUsers: mentions.map((m) => ({ userId: m.id, name: m.name })),
      },
      { onSuccess, onSettled },
    );
  };

  // Per-suggestion pending bookkeeping shared by accept and reject.
  const trackPending = (key) => {
    setPending((prev) => new Set([...prev, key]));
    return () =>
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
  };

  // Prepend an optimistic row to the customer's activity feed; the realtime ActivityCreated hint
  // reconciles the canonical feed.
  const prependActivity = (customerId, row) => {
    if (!customerId) return;
    queryClient.setQueryData(["customerActivities", customerId], (old) => {
      if (!old?.pages?.length) return old;
      const [firstPage, ...rest] = old.pages;
      return {
        ...old,
        pages: [{ ...firstPage, items: [row, ...(firstPage?.items ?? [])] }, ...rest],
      };
    });
    revealActivity(row?.id);
  };

  // Signal the layout: reveal the Activity tab and animate this row in.
  const revealActivity = (rowId) => {
    if (rowId) setEnterActivityId(rowId);
    setActivityPulse((n) => n + 1);
  };

  // Approve a suggested action through the BFF → intelligence-service.
  const acceptAction = (
    message,
    signalIndex,
    suggestion,
    { amendments, onApplied, onNotApplied } = {},
  ) => {
    const key = `${message.id}:${signalIndex}`;
    const markDone = () => setDone((prev) => new Set([...prev, key]));
    const onSettled = trackPending(key);

    decideAction.mutate(
      {
        actionId: suggestion.params?.actionId,
        conversationId: message.conversationId,
        customerId: analysedCustomer?.id,
        accept: true,
        amendments,
      },
      {
        onSuccess: (reply) => {
          if (reply.outcome !== "Applied") {
            if (onNotApplied) onNotApplied(reply);
            else
              toast({
                description:
                  reply.message ??
                  "The suggestion couldn't be applied — ask again in the chat.",
              });
            return;
          }
          onApplied?.();
          markDone();
          const note = suggestion.params?.note;
          if (
            !getSuggestionComponent(suggestion.type) &&
            typeof note === "string" &&
            note.trim()
          ) {
            appendSuggestion(message.id, {
              type: "CustomerInteractionNoted",
              label: "Add note",
              params: { summary: note.trim() },
            });
          }

          if (suggestion.type === "PaymentRequestRequested") {
            const step = editablePaymentStep(suggestion.params?.steps);
            const amendedPayload = step && amendments?.[step.stepId];
            const statement = isStatementStep(amendedPayload ?? step?.payload);
            const sentText = statement ? "Statement sent" : "Payment request sent";
            toast({ description: sentText });
            prependActivity(analysedCustomer?.id, {
              id: `optimistic-pr-${Date.now()}`,
              eventName: "PaymentRequestSent",
              title: sentText,
              amount: statement
                ? null
                : (amendedPayload?.amount_requested ??
                  paymentRequestAmount(suggestion.params?.steps)),
              invoicesRequested:
                (amendedPayload ?? step?.payload)?.invoices_requested ?? null,
              occurredAt: new Date().toISOString(),
            });
          }

          if (
            (suggestion.type === "PromiseToPay" || suggestion.type === "PaymentPlan") &&
            suggestion.label
          ) {
            toast({ description: `${suggestion.label} started` });
            prependActivity(analysedCustomer?.id, {
              id: `optimistic-action-${suggestion.params?.actionId ?? Date.now()}`,
              eventName: "ActionStarted",
              title: `${suggestion.label} started`,
              body: suggestion.params?.explanation ?? null,
              occurredAt: new Date().toISOString(),
            });
          }

          if (isSendEmailType(suggestion.type)) {
            const attached = suggestion.params?.requestConfig?.documents?.length ?? 0;
            toast({ description: attached > 0 ? "Documents sent" : "Email sent" });
          }
        },
        onError: () =>
          toast({
            description: "Something went wrong and nothing was sent — please try again.",
          }),
        onSettled,
      },
    );
  };

  // Accept a registry-driven suggestion component (see suggestionComponents.js).
  const handleAcceptComponent = (message, signalIndex, suggestion, descriptor, values) => {
    if (!analysedCustomer) return;

    if (descriptor.acceptVia === "task") {
      const key = `${message.id}:${signalIndex}`;
      const onSettled = trackPending(key);
      const request = descriptor.buildAcceptRequest(values);
      acceptTask.mutate(
        {
          actionId: suggestion.params?.actionId,
          conversationId: message.conversationId,
          customerId: analysedCustomer?.id,
          ...request,
        },
        {
          onSuccess: (reply) => {
            if (reply.outcome !== "Applied") {
              toast({
                description:
                  reply.message ?? "The task couldn't be created — ask again in the chat.",
              });
              return;
            }
            descriptor.onAccepted?.({ queryClient, suggestion });
            if (descriptor.successMessage) toast({ description: descriptor.successMessage });
            setDone((prev) => new Set([...prev, key]));

            prependActivity(analysedCustomer?.id, {
              id: `optimistic-task-${suggestion.params?.actionId ?? Date.now()}`,
              eventName: "TaskCreated",
              title: request.title || "Task",
              scheduledFor: request.dueAt ?? null,
              taggedUsers: (values.assignees ?? []).map((assignee) => ({
                userId: assignee.id,
                name: assignee.name,
              })),
              occurredAt: new Date().toISOString(),
            });

            contextPanelRef.current?.reconcileActivity();
          },
          onError: () =>
            toast({
              description: "Something went wrong and nothing was created — please try again.",
            }),
          onSettled,
        },
      );
      return;
    }

    acceptAction(message, signalIndex, suggestion, {
      amendments: descriptor.buildAmendments(suggestion, values),
      onApplied: () => {
        descriptor.onAccepted?.({ queryClient, suggestion });
        if (descriptor.successMessage) toast({ description: descriptor.successMessage });
      },
      onNotApplied: (reply) => toast({ description: reply.message }),
    });
  };

  // Edit-then-accept: open the batch request modal / email preview seeded from the suggestion.
  const handleEditSuggestion = (message, signalIndex, suggestion) => {
    if (!analysedCustomer) return;
    if (isSendEmailType(suggestion.type)) {
      setEditDocsTarget({ message, signalIndex, suggestion });
      return;
    }
    setEditTarget({ message, signalIndex, suggestion });
  };

  const closeEdit = () => setEditTarget(null);
  const closeDocsEdit = () => setEditDocsTarget(null);

  const handleOpenPaymentRequest = useCallback(
    (id) => {
      if (!id) return;
      setPaymentRequestId(id);
      onClose();
    },
    [onClose],
  );

  const openPaymentRequestFromPlan = useCallback((id) => {
    if (!id) return;
    setPlanActionId(null);
    setPaymentRequestId(id);
  }, []);

  const handleAcceptDocsEdited = ({
    subject,
    title,
    body,
    attachmentLocations,
    recipients,
    cc,
    bcc,
  }) => {
    if (!editDocsTarget) return;
    const { message, signalIndex, suggestion } = editDocsTarget;
    acceptAction(message, signalIndex, suggestion, {
      amendments: buildEmailAmendments({
        steps: suggestion.params?.steps,
        subject,
        title,
        body,
        attachmentLocations,
        recipients,
        cc,
        bcc,
      }),
      onApplied: closeDocsEdit,
      onNotApplied: (reply) => toast({ description: reply.message }),
    });
  };

  const handleAcceptEdited = ({ values, strategy, account, cc, bcc }) => {
    if (!editTarget) return;
    const { message, signalIndex, suggestion } = editTarget;
    acceptAction(message, signalIndex, suggestion, {
      amendments: buildAmendments({
        steps: suggestion.params?.steps,
        values,
        strategy,
        account,
        cc,
        bcc,
      }),
      onApplied: closeEdit,
      onNotApplied: (reply) => toast({ description: reply.message }),
    });
  };

  const editStep = editTarget
    ? editablePaymentStep(editTarget.suggestion.params?.steps)
    : null;

  const handleAcceptSuggestion = (message, signalIndex, suggestion, content, mentions) => {
    const key = `${message.id}:${signalIndex}`;
    const markDone = () => setDone((prev) => new Set([...prev, key]));

    if (suggestion.type === "CancellationRequested") {
      setCancelTarget({ message, signalIndex, suggestion });
      return;
    }

    if (isActionBacked(suggestion.type)) {
      acceptAction(message, signalIndex, suggestion);
      return;
    }

    if (suggestion.type === "CustomerInteractionNoted") {
      handleSave(
        content ?? suggestion.params?.summary ?? "",
        mentions ?? message.mentions ?? [],
        message.conversationId,
        (note) => {
          markDone();
          // useSaveSmartNote already wrote the optimistic row under this id; just reveal it.
          revealActivity(`optimistic-note-${note?.id ?? Date.now()}`);
        },
        trackPending(key),
      );
      return;
    }
  };

  const performCancellation = () => {
    if (!cancelTarget) return;
    const { message, signalIndex, suggestion } = cancelTarget;
    const key = `${message.id}:${signalIndex}`;
    const markDone = () => setDone((prev) => new Set([...prev, key]));
    const onSettled = trackPending(key);
    cancelAction.mutate(
      {
        actionId: suggestion.params?.targetActionId,
        conversationId: message.conversationId,
        customerId: analysedCustomer?.id,
        scope: suggestion.params?.scope,
      },
      {
        onSuccess: (reply) => {
          toast({ description: reply.message });
          if (reply.outcome !== "Applied") return;
          markDone();
          const copy = cancelledActivityCopy(suggestion.params);
          prependActivity(analysedCustomer?.id, {
            id: `optimistic-cancel-${suggestion.params?.targetActionId ?? Date.now()}`,
            eventName: "ActionCancelled",
            title: copy.title,
            body: copy.body,
            occurredAt: new Date().toISOString(),
          });
        },
        onError: () =>
          toast({
            description:
              "Something went wrong and nothing was cancelled — please try again.",
          }),
        onSettled: () => {
          onSettled();
          setCancelTarget(null);
        },
      },
    );
  };

  const handleRejectSuggestion = (message, signalIndex, suggestion) => {
    const key = `${message.id}:${signalIndex}`;
    const markRejected = () => setRejected((prev) => new Set([...prev, key]));

    const actionId = suggestion.params?.actionId;
    const notExpired =
      !suggestion.params?.expiresAt ||
      new Date(suggestion.params.expiresAt) > new Date();
    if (!actionId || !notExpired) {
      markRejected();
      return;
    }

    const onSettled = trackPending(key);
    decideAction.mutate(
      {
        actionId,
        conversationId: message.conversationId,
        customerId: analysedCustomer?.id,
        accept: false,
      },
      {
        onSuccess: markRejected,
        onError: () =>
          toast({
            description:
              "Something went wrong and the rejection wasn't recorded — please try again.",
          }),
        onSettled,
      },
    );
  };

  // Clear everything when the flow closes.
  useEffect(() => {
    if (!isOpen) {
      clearConversation();
      setSelectedCustomer(null);
      setDraft(EMPTY_DRAFT);
      remountEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return {
    // state
    draft,
    selectedCustomer,
    setSelectedCustomer,
    analysedCustomer,
    requestCustomer,
    editorKey,
    composerSeed,
    acceptAutocompleteSuggestion,
    sendAutocompleteSuggestion,
    done,
    rejected,
    pending,
    editTarget,
    editDocsTarget,
    editStep,
    preview,
    setPreview,
    cancelTarget,
    setCancelTarget,
    paymentRequestId,
    setPaymentRequestId,
    planActionId,
    setPlanActionId,
    readReady,
    setReadReady,
    canSubmit,
    contextPanelRef,
    clearConversation,
    loadConversation,
    loadingConversationId,
    prependActivity,
    enterActivityId,
    activityPulse,
    // chat
    messages,
    isThinking,
    thinkingLabel,
    activeConversationId,
    lastCardMs,
    // mutations (for pending flags)
    decideAction,
    cancelAction,
    // handlers
    handleDraftChange,
    handleSubmit,
    handleAcceptComponent,
    handleEditSuggestion,
    handleAcceptSuggestion,
    handleRejectSuggestion,
    handleAcceptEdited,
    handleAcceptDocsEdited,
    performCancellation,
    handleOpenPaymentRequest,
    openPaymentRequestFromPlan,
    closeEdit,
    closeDocsEdit,
  };
}
